import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  MembersListDataDocument,
  RemoveAllUserItemSharesDocument,
  RemoveAllUserWorkflowSharesDocument,
  ShareItemDocument,
  ShareWorkflowDocument,
  type MemberAccessList,
  type ObjectAccessLevel,
  UserAccessListDocument,
} from "#app/lib/data";

// Apollo plumbing for the settings members table's Access popover —
// the per-user inverse of the share dialogs. Reads `userAccessList`
// (granted workflows + add candidates + granted items) and reuses the
// shareWorkflow / shareItem upserts for level changes, removes (toggle
// off), and adds (toggle on). Every mutation refetches the access list
// AND the members list so the row's "N Workflows  N Items" counts stay
// in sync.

export type { MemberAccessList };

export function useMemberAccess(userId: string | null) {
  const variables = useMemo(() => ({ userId: userId ?? "" }), [userId]);
  const { data, loading } = useQuery(UserAccessListDocument, {
    variables,
    skip: !userId,
    fetchPolicy: "cache-and-network",
  });

  // Refetch by document so every active observation (the popover's
  // list + the paginated members table) refreshes regardless of its
  // variables.
  const refetchQueries = [UserAccessListDocument, MembersListDataDocument];
  const [shareWorkflowMutation] = useMutation(ShareWorkflowDocument, {
    refetchQueries,
  });
  const [shareItemMutation] = useMutation(ShareItemDocument, {
    refetchQueries,
  });
  const [removeAllWorkflowsMutation] = useMutation(
    RemoveAllUserWorkflowSharesDocument,
    { refetchQueries },
  );
  const [removeAllItemsMutation] = useMutation(
    RemoveAllUserItemSharesDocument,
    { refetchQueries },
  );

  // Workflow grants: add / level-change keep enabled=true; remove
  // flips enabled=false (row + level preserved so re-adding restores
  // the prior tier).
  const setWorkflowAccess = useCallback(
    (workflowId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!userId) return;
      void shareWorkflowMutation({
        variables: {
          input: { workflowId, userId, accessLevel, enabled },
        },
      });
    },
    [shareWorkflowMutation, userId],
  );

  const setItemAccess = useCallback(
    (issueId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!userId) return;
      void shareItemMutation({
        variables: {
          input: { issueId, userId, accessLevel, enabled },
        },
      });
    },
    [shareItemMutation, userId],
  );

  const removeAllWorkflows = useCallback(() => {
    if (!userId) return;
    void removeAllWorkflowsMutation({ variables: { userId } });
  }, [removeAllWorkflowsMutation, userId]);

  const removeAllItems = useCallback(() => {
    if (!userId) return;
    void removeAllItemsMutation({ variables: { userId } });
  }, [removeAllItemsMutation, userId]);

  return {
    loading,
    accessList: (data?.userAccessList ?? null) as MemberAccessList | null,
    setWorkflowAccess,
    setItemAccess,
    removeAllWorkflows,
    removeAllItems,
  };
}
